import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalGuardAuthRequest, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { JwtHelperService } from "@auth0/angular-jwt";
import { AuthenticationResult, SilentRequest } from "@azure/msal-browser";
import { PvUserSession } from "@models/user-session";
import { MsalJwtToken } from "@models/jwt-token";
import { Numbers } from "@constants/numbers";
import * as moment from "moment/moment";
import { UserSessionService } from "@services/user-session/user-session.service";

@Injectable({
  providedIn: 'root',
})
export class  AuthenticationService {

  private jwtUtil: JwtHelperService;

  constructor(
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfiguration: MsalGuardConfiguration,
    private userSessionService: UserSessionService,
  ) {
    this.jwtUtil = new JwtHelperService();
  }

  setupToken(): void {
    const key = Object.keys(localStorage).find((k) => k.includes('idtoken')) ?? String();
    const dataToken = JSON.parse(localStorage.getItem(key) ?? JSON.stringify({}));
    localStorage.setItem(btoa("msal.idToken") , btoa(dataToken.secret));
  }


  extractMSALTokenAccess(): string | any | null {
    const key = Object.keys(localStorage).find((k) => k.includes('accesstoken')) ?? String();
    const data = JSON.parse(localStorage.getItem(key) ?? JSON.stringify({}));
    if (data.secret === null || data.secret === undefined) {
      localStorage.removeItem(key);
      return null;
    }
    return data.secret;
  }

  isAuthenticated(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  getName(): string {
    return this.msalService.instance.getAllAccounts()[0]?.name ?? String();
  }

  getUserEmail(): string {
    return this.msalService.instance.getAllAccounts()[0]?.username;
  }

  getUserName(): string {
    const userInfo: string = this.msalService.instance.getAllAccounts()[0]?.username;
    return userInfo?.split("@")[0];
  }

  getToken(): string {
    const encryptedToken =  localStorage.getItem(btoa("msal.idToken")) ?? String()
    return atob(encryptedToken);
  }

  getAccessToken(): string {
    const encryptedAccessToken = localStorage.getItem(btoa("msal.idToken")) ?? String()
    return atob(encryptedAccessToken);
  }

  isExpiredToken(): boolean {
    return this.jwtUtil.isTokenExpired(this.getToken());
  }

  acquireTokenSilent() {
    if (this.isAuthenticated()) {
      this.msalService.acquireTokenSilent(this.getRequest())
        .subscribe({
          next: (response: AuthenticationResult) => {
            this.updateSession(response.idToken);
          }, error: (err: any) => {
            this.msalService.logoutRedirect();
          }});
    }
  }

  getRequest(): SilentRequest {
    return {
      scopes: (this.msalGuardConfiguration.authRequest as MsalGuardAuthRequest).scopes!,
      account: this.isAuthenticated() ? this.msalService.instance.getAllAccounts()[0] : undefined,
      forceRefresh: true
    }
  }

  updateSession(idToken: string): void {
    const tokenInfo = this.jwtUtil.decodeToken(idToken) ?? {} as MsalJwtToken;
    const userSession: PvUserSession = {
      userEmail: this.getUserEmail(),
      jwtToken: idToken,
      createdAt: this.formatMilliseconds(tokenInfo.iat),
      expiredAt: this.formatMilliseconds(tokenInfo.exp),
      isActive: true
    };
    this.userSessionService.save(userSession)
      .subscribe({
        next: (userSession: PvUserSession) => {
          localStorage.setItem(btoa("msal.idToken"), btoa(userSession.jwtToken));
        }
      });
  }

  formatMilliseconds(millisegs: number): string {
    const date = new Date(millisegs * Numbers._1000);
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }
}
